// Browser image helpers for the report logo: downscale a too-large upload, and
// render a square crop from a stored (zoom, center) transform so the crop can be
// reopened and adjusted later. The crop math is a pure function so it is unit-
// testable without a canvas.

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Could not read the image.'))
    reader.readAsDataURL(file)
  })
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load the image.'))
    img.src = src
  })
}

// Shrink an uploaded image so its longest side is at most maxDim, re-encoding as
// PNG (preserves logo transparency). Small images pass through untouched.
export async function downscaleToDataUrl(file, maxDim = 600) {
  const dataUrl = await readFileAsDataUrl(file)
  const img = await loadImage(dataUrl)
  const longest = Math.max(img.naturalWidth, img.naturalHeight)
  const scale = Math.min(1, maxDim / longest)
  if (scale === 1) return dataUrl
  const w = Math.max(1, Math.round(img.naturalWidth * scale))
  const h = Math.max(1, Math.round(img.naturalHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  canvas.getContext('2d').drawImage(img, 0, 0, w, h)
  return canvas.toDataURL('image/png')
}

// The default crop: the whole image centered at 1x zoom.
export const DEFAULT_CROP = { zoom: 1, cx: 0.5, cy: 0.5 }

// Resolve a (zoom, center) crop into the source-pixel square to draw, clamped so
// the square always stays within the image. Pure – no DOM.
export function cropSourceRect(naturalW, naturalH, crop = DEFAULT_CROP) {
  const zoom = Math.max(1, Number(crop?.zoom) || 1)
  const minSide = Math.min(naturalW, naturalH)
  const size = minSide / zoom
  const half = size / 2
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
  const cx = clamp(Number(crop?.cx ?? 0.5), 0, 1)
  const cy = clamp(Number(crop?.cy ?? 0.5), 0, 1)
  const sx = clamp(cx * naturalW - half, 0, naturalW - size)
  const sy = clamp(cy * naturalH - half, 0, naturalH - size)
  return { sx, sy, size }
}

// Render the square crop to a data URL the PDF/preview can use.
export async function renderSquareCrop(sourceDataUrl, crop, output = 512) {
  const img = await loadImage(sourceDataUrl)
  const { sx, sy, size } = cropSourceRect(img.naturalWidth, img.naturalHeight, crop)
  const canvas = document.createElement('canvas')
  canvas.width = output
  canvas.height = output
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, output, output)
  ctx.drawImage(img, sx, sy, size, size, 0, 0, output, output)
  return canvas.toDataURL('image/png')
}
